// Utility to parse buy request descriptions that contain pipe-delimited metadata

export interface BuyRequestMetadata {
  cleanDescription: string;
  urgency?: string;
  financing?: string;
  radius?: string;
}

/**
 * Parses engine buy request descriptions that contain metadata in format:
 * "User description | Urgency: X | Financing: Y | Radius: Z"
 * 
 * @param description - The raw description string from the database
 * @returns Parsed metadata with clean description and extracted fields
 */
export function parseBuyRequestDescription(description?: string): BuyRequestMetadata {
  if (!description) {
    return { cleanDescription: '' };
  }

  // Split by pipe delimiter
  const parts = description.split('|').map(part => part.trim());
  
  const metadata: BuyRequestMetadata = {
    cleanDescription: parts[0] || '', // First part is always the user's actual description
  };

  // Parse remaining parts for metadata
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i];
    
    if (part.startsWith('Urgency:')) {
      metadata.urgency = part.replace('Urgency:', '').trim();
    } else if (part.startsWith('Financing:')) {
      metadata.financing = part.replace('Financing:', '').trim();
    } else if (part.startsWith('Radius:')) {
      metadata.radius = part.replace('Radius:', '').trim();
    }
  }

  return metadata;
}

/**
 * Gets a display-friendly label for urgency level
 */
export function getUrgencyLabel(urgency?: string): string {
  if (!urgency) return '';
  
  switch (urgency.toLowerCase()) {
    case 'immediate':
      return 'Immediate';
    case 'within_month':
      return 'Within 1 Month';
    case 'within_3_months':
      return 'Within 3 Months';
    case 'flexible':
      return 'Flexible Timeline';
    default:
      return urgency;
  }
}

/**
 * Gets the appropriate color/variant for urgency badges
 */
export function getUrgencyVariant(urgency?: string): 'default' | 'destructive' | 'secondary' {
  if (!urgency) return 'secondary';
  
  switch (urgency.toLowerCase()) {
    case 'immediate':
      return 'destructive';
    case 'within_month':
      return 'default';
    default:
      return 'secondary';
  }
}
